import * as React from 'react'
import { CircleAlert } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

type FieldOwnProps = {
  /** Always visible, above the control. The placeholder only gives an example. */
  label: string
  /** Help under the label. */
  help?: React.ReactNode
  /**
   * What to fix. The form decides when to show it (on blur, keeping what the
   * person typed); the field marks the control `aria-invalid` and links it.
   */
  error?: string
  className?: string
  /** For the control itself (a code field centres and spaces its digits). */
  inputClassName?: string
}

type InputFieldProps = FieldOwnProps &
  Omit<React.ComponentProps<'input'>, 'className'> & { multiline?: false }

type TextareaFieldProps = FieldOwnProps &
  Omit<React.ComponentProps<'textarea'>, 'className'> & { multiline: true }

export type FieldProps = InputFieldProps | TextareaFieldProps

/**
 * Text field of Alumnia UI 1.2.1 (docs/redesign/sistema/Field.md): visible
 * label, help and error around the Encuentro `Input` (or `Textarea` with
 * `multiline`). Text is 16 px so iOS does not zoom; the help and the error
 * are announced with the control through `aria-describedby`.
 */
export function Field(props: FieldProps) {
  const autoId = React.useId()
  const { label, help, error, className, inputClassName, id = autoId } = props
  const helpId = help ? `${id}-help` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy =
    [props['aria-describedby'], helpId, errorId].filter(Boolean).join(' ') || undefined

  const control = {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : props['aria-invalid'],
  }

  let field: React.ReactNode
  if (props.multiline) {
    const {
      label: _l,
      help: _h,
      error: _e,
      className: _c,
      inputClassName: _i,
      multiline: _m,
      ...rest
    } = props
    field = <Textarea look="encuentro" className={inputClassName} {...rest} {...control} />
  } else {
    const {
      label: _l,
      help: _h,
      error: _e,
      className: _c,
      inputClassName: _i,
      multiline: _m,
      ...rest
    } = props
    field = <Input look="encuentro" className={inputClassName} {...rest} {...control} />
  }

  return (
    <div
      data-slot="field"
      className={cn('grid content-start gap-2 font-en-sans text-en-ink', className)}
    >
      <label htmlFor={id} className="text-en-label">
        {label}
      </label>
      {field}
      {help && (
        <p id={helpId} className="text-en-meta text-en-ink-muted">
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="flex items-start gap-2 text-en-meta text-en-danger">
          <CircleAlert aria-hidden className="mt-px size-4 shrink-0" />
          <span>{error}</span>
        </p>
      )}
    </div>
  )
}
